/**
 * Copyright (C) 2018-present, Botobag Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *      http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import assert from 'assert';

import {
  Image,
  ImageData,
  ImageTag,
} from './image';
import {
  ProductOption,
  ProductOptionData,
} from './productOption';
import {
  ProductVariant,
  ProductVariantData,
} from './productVariant';
import { Money } from '@/lib/money';

export interface ProductData {
  readonly id: string;
  readonly name: string;
  readonly images?: ImageData[];
  readonly featureBullets?: string[];
  readonly brand?: string;
  readonly priceCurrency: string;
  readonly options?: ProductOptionData[];
  readonly variants: ProductVariantData[];
}

export class Product {
  public readonly images?: Image[];
  public readonly options?: ProductOption[];
  public readonly variants: ProductVariant[];

  constructor(private readonly data: ProductData) {
    if (data.images) {
      this.images = data.images.map((image) => (new Image(image)));
    }

    if (!data.options) {
      assert.ok(data.variants.length === 1,
        'only one variant is allowed in product without options');
      assert.ok(!data.variants[0].options || data.variants[0].options!.length === 0,
        'master variant should not have options');
    } else {
      assert.ok(!data.variants.find((variant) =>
        (!variant.options || variant.options.length !== data.options!.length)),
        'find an invalid variant with unmatched number of options');

      this.options = data.options.map((optionData, index) => {
        const values = [...new Set(data.variants.map((variant) => ((variant.options!)[index])))];
        return new ProductOption(optionData, values);
      });
    }

    this.variants = data.variants.map((variant) => (new ProductVariant(this, variant)));
  }

  get id() { return this.data.id; }
  get name() { return this.data.name; }
  get image() { return this.getImage(ImageTag.MainImage); }
  get featureBullets() { return this.data.featureBullets; }
  get brand() { return this.data.brand; }
  get priceCurrency() { return this.data.priceCurrency; }
  get priceRange(): [Money, Money] | undefined {
    let min = this.variants[0].price;
    if (min) {
      let max = min;
      for (let i = 1, e = this.variants.length; i < e; ++i) {
        const price = this.variants[i].price;
        if (price) {
          if (price.amount.lessThan(min.amount)) {
            min = price;
          } else if (price.amount.greaterThan(max.amount)) {
            max = price;
          }
        }
      }
      return [min, max];
    }
    return undefined;
  }

  public getImage(tag: string) {
    return (this.images) ? this.images.find((image) => (image.tag === tag)) : undefined;
  }
}
